import axios from "axios"

export interface EmbedConfig {
  id: string
  embedUrl: string
  accessToken: string
  tokenExpiry: string
}

export interface PowerBIReport {
  id: string
  name: string
  embedUrl: string
  datasetId: string
}

export interface PowerBIWorkspace {
  id: string
  name: string
  isReadOnly: boolean
  isOnDedicatedCapacity: boolean
}

export interface PowerBIDataset {
  id: string
  name: string
  addRowsAPIEnabled: boolean
  configuredBy: string
  isRefreshable: boolean
}

export class PowerBIService {
  private baseUrl = "https://api.powerbi.com/v1.0/myorg"

  constructor(private accessToken: string) { }

  private getHeaders() {
    return {
      Authorization: `Bearer ${this.accessToken}`,
      "Content-Type": "application/json",
    }
  }

  // Test API access with detailed permission checking
  async testApiAccess(): Promise<{ success: boolean; message: string; details?: any }> {
    const tests = [
      { name: "Groups", url: `${this.baseUrl}/groups` },
      { name: "Reports", url: `${this.baseUrl}/reports` },
      { name: "Datasets", url: `${this.baseUrl}/datasets` },
    ]

    const results: any = {}

    for (const test of tests) {
      try {
        const response = await axios.get(test.url, {
          headers: this.getHeaders(),
        })
        results[test.name] = { success: true, count: response.data.value?.length || 0 }
      } catch (error: any) {
        results[test.name] = {
          success: false,
          error: error.response?.status,
          message: error.response?.data?.error?.message || error.message,
        }
      }
    }

    const allSuccess = Object.values(results).every((r: any) => r.success)

    return {
      success: allSuccess,
      message: allSuccess ? "All API tests passed" : "Some API tests failed",
      details: results,
    }
  }

  async getWorkspaces(): Promise<PowerBIWorkspace[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/groups`, {
        headers: this.getHeaders(),
      })
      return response.data.value
    } catch (error: any) {
      console.error("Error fetching workspaces:", error.response?.data || error.message)
      this.handleApiError(error, "fetch workspaces")
      return []
    }
  }

  async getReports(workspaceId?: string): Promise<PowerBIReport[]> {
    try {
      const url = workspaceId ? `${this.baseUrl}/groups/${workspaceId}/reports` : `${this.baseUrl}/reports`

      const response = await axios.get(url, { headers: this.getHeaders() })
      return response.data.value
    } catch (error: any) {
      console.error("Error fetching reports:", error.response?.data || error.message)
      this.handleApiError(error, "fetch reports")
      return []
    }
  }

  async getDatasets(workspaceId?: string): Promise<PowerBIDataset[]> {
    try {
      const url = workspaceId ? `${this.baseUrl}/groups/${workspaceId}/datasets` : `${this.baseUrl}/datasets`

      const response = await axios.get(url, { headers: this.getHeaders() })
      return response.data.value
    } catch (error: any) {
      console.error("Error fetching datasets:", error.response?.data || error.message)
      this.handleApiError(error, "fetch datasets")
      return []
    }
  }

  async getEmbedToken(workspaceId: string, reportId: string): Promise<EmbedConfig> {
    try {
      // First, get the report details to ensure we have access
      const reportUrl = `${this.baseUrl}/groups/${workspaceId}/reports/${reportId}`
      const reportResponse = await axios.get(reportUrl, {
        headers: this.getHeaders(),
      })

      const report = reportResponse.data

      // Get the dataset ID from the report
      const datasetId = report.datasetId

      // Generate embed token with both report and dataset access
      const tokenUrl = `${this.baseUrl}/groups/${workspaceId}/reports/${reportId}/GenerateToken`

      const requestBody = {
        accessLevel: "View",
        allowSaveAs: false,
        // Include dataset in the token request
        datasets: [
          {
            id: datasetId,
          },
        ],
        reports: [
          {
            allowEdit: false,
            id: reportId,
          },
        ],
      }

      console.log("Generating embed token with payload:", requestBody)

      const tokenResponse = await axios.post(tokenUrl, requestBody, {
        headers: this.getHeaders(),
      })

      return {
        id: reportId,
        embedUrl: report.embedUrl,
        accessToken: tokenResponse.data.token,
        tokenExpiry: tokenResponse.data.expiration,
      }
    } catch (error: any) {
      console.error("Error generating embed token:", error.response?.data || error.message)

      if (error.response?.status === 401) {
        throw new Error("Unauthorized: Please check your authentication token")
      }

      if (error.response?.status === 403) {
        const errorMessage = error.response?.data?.error?.message || ""
        if (errorMessage.includes("Report.Read.All") || errorMessage.includes("Dataset.Read.All")) {
          throw new Error(
            "Missing required permissions. Please ensure your Azure AD app has 'Report.Read.All' and 'Dataset.Read.All' permissions and admin consent has been granted.",
          )
        }
        throw new Error(`Forbidden: ${errorMessage}`)
      }

      if (error.response?.status === 404) {
        throw new Error("Report or workspace not found. Please check your workspace ID and report ID.")
      }

      const errorMessage = error.response?.data?.error?.message || error.message
      throw new Error(`Failed to generate embed token: ${errorMessage}`)
    }
  }

  // Alternative method using different API endpoint
  async getEmbedTokenAlternative(workspaceId: string, reportId: string): Promise<EmbedConfig> {
    try {
      // Use the alternative embed token generation endpoint
      const tokenUrl = `${this.baseUrl}/GenerateToken`

      // Get report and dataset info first
      const reportUrl = `${this.baseUrl}/groups/${workspaceId}/reports/${reportId}`
      const reportResponse = await axios.get(reportUrl, {
        headers: this.getHeaders(),
      })

      const report = reportResponse.data
      const datasetId = report.datasetId

      const requestBody = {
        datasets: [{ id: datasetId }],
        reports: [{ id: reportId }],
        targetWorkspaces: [{ id: workspaceId }],
      }

      console.log("Generating embed token (alternative) with payload:", requestBody)

      const tokenResponse = await axios.post(tokenUrl, requestBody, {
        headers: this.getHeaders(),
      })

      return {
        id: reportId,
        embedUrl: report.embedUrl,
        accessToken: tokenResponse.data.token,
        tokenExpiry: tokenResponse.data.expiration,
      }
    } catch (error: any) {
      console.error("Error generating embed token (alternative):", error.response?.data || error.message)
      throw error
    }
  }

  private handleApiError(error: any, operation: string) {
    if (error.response?.status === 401) {
      throw new Error(`Unauthorized: Cannot ${operation}. Please check your authentication.`)
    }
    if (error.response?.status === 403) {
      const errorMessage = error.response?.data?.error?.message || ""
      throw new Error(`Forbidden: Insufficient permissions to ${operation}. ${errorMessage}`)
    }
    if (error.response?.status === 404) {
      throw new Error(`Not Found: Cannot ${operation}. Resource not found.`)
    }

    const errorMessage = error.response?.data?.error?.message || error.message
    throw new Error(`Failed to ${operation}: ${errorMessage}`)
  }

  async refreshEmbedToken(workspaceId: string, reportId: string): Promise<string> {
    try {
      const embedConfig = await this.getEmbedToken(workspaceId, reportId)
      return embedConfig.accessToken
    } catch (error) {
      console.error("Error refreshing embed token:", error)
      throw error
    }
  }

  async refreshDataset(workspaceId: string, datasetId: string): Promise<void> {
    try {
      const url = `${this.baseUrl}/groups/${workspaceId}/datasets/${datasetId}/refreshes`
      await axios.post(url, {}, { headers: this.getHeaders() })
    } catch (error: any) {
      console.error("Error refreshing dataset:", error.response?.data || error.message)
      this.handleApiError(error, "refresh dataset")
    }
  }
}
